/**
 * The ledger at a glance, for the chrome: the sidebar's count pill.
 *
 * The read is an aggregate over a table that grows forever, and the sidebar
 * mounts on every signed-in navigation, so a successful answer is cached per
 * page load (the same discipline the old Library reveal probe kept) and
 * refreshed only when this tab writes to the ledger
 * (`invalidateDecisionsSummary`, which the Decisions surface calls after
 * every confirm / supersede / discard / record). A failed read is never
 * cached: one transient error must not hide the pill for the whole session,
 * so the next mount asks again, and the rejection is logged rather than
 * swallowed. A failure or an empty ledger reads as `null`, so the caller
 * hides rather than lies.
 */
import { useEffect, useState } from "react";
import type { CodeDecisionsSummary } from "@yagni/shared";
import { decisionsApi, type DecisionsApi } from "./decisionsApi.js";

const DECISIONS_CHANGED_EVENT = "yagni:decisions-changed";

let cached: CodeDecisionsSummary | undefined;
let inFlight: Promise<CodeDecisionsSummary | null> | null = null;

/** Tests mount the chrome repeatedly in one process; the cache must not leak. */
export function __resetDecisionsSummaryCache(): void {
  cached = undefined;
  inFlight = null;
}

/** Drop the cached answer and tell every mounted chrome to re-read. */
export function invalidateDecisionsSummary(): void {
  cached = undefined;
  inFlight = null;
  if (typeof window !== "undefined") window.dispatchEvent(new Event(DECISIONS_CHANGED_EVENT));
}

function readSummary(api: DecisionsApi): Promise<CodeDecisionsSummary | null> {
  if (cached !== undefined) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = api
      .summary()
      .then((loaded) => {
        cached = loaded;
        return loaded;
      })
      .catch((err: unknown) => {
        // Not cached: the next mount asks again.
        console.warn("[decisions] chrome count unavailable", err);
        return null;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

export function useDecisionsSummary(api: DecisionsApi = decisionsApi): CodeDecisionsSummary | null {
  const [summary, setSummary] = useState<CodeDecisionsSummary | null>(cached ?? null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void readSummary(api).then((loaded) => {
        if (!cancelled) setSummary(loaded);
      });
    };
    load();
    window.addEventListener(DECISIONS_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(DECISIONS_CHANGED_EVENT, load);
    };
  }, [api]);
  return summary;
}
